import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import StructureFilter from '@/components/Custom/Filter/StructureFilter';
import { TableViewProps } from '@/type/listing.types';
import { mapFilterSelect } from '@/utils/map.utils';
import { useStructureFilter } from '@/hooks/filters/structure-filter.hooks';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { StructureType } from '@/entities/structure/common';
import { usePageSize } from '@/hooks/page-size.hooks';
import { STRUCTURE_TIPS } from '@/docs/tips/structure';
import { RichSelect } from '@/components/Controls/RichSelect';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
} from '@/components/Custom/Filter/FilterKit';
import { getUsageFilterOptions } from '@/components/Custom/Usage';

export const GroupTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const { primaryRole, loggedInUser } = useAuth();
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { pageSize: limit, setPageSize } = usePageSize();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    type,
    searchByKey,
    searchByTypes,
    startDate,
    endDate,
    isWhiteMark,
    companyId,
    branchId,
    searchBeginDate,
    searchEndDate,
    searchWhiteMark,
    searchByCompany,
    searchByBranch,
  } = useStructureFilter({
    structureType: StructureType.GROUP,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      role: primaryRole !== RoleName.ADMIN ? primaryRole : undefined,
      userId: loggedInUser?.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryRole, loggedInUser?.id]);
  const advancedFiltersCount = [
    startDate || endDate,
    isWhiteMark === 'true',
    companyId,
    branchId,
  ].filter(Boolean).length;
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      groupId: debouncedFilter?.groupId,
    });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              textClassName="mt-1.5"
              text={t('common:groups')}
              infoTip={STRUCTURE_TIPS.groupsTable}
            />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <div className="flex items-center flex-1 gap-3.5 min-w-[300px] max-1.5xl:min-w-0 max-1.5xl:basis-full">
              <Input
                placeholder={t('searchGroup')}
                type="text"
                name="search"
                autoComplete="off"
                leftIcon={
                  <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
                }
                inputWrapperClassName="w-full"
                inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                value={key}
                onChange={(e) => searchByKey(e.target.value)}
              />
              <div className="hidden max-1.5xl:block max-md:hidden">
                <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
              </div>
            </div>
            <RichSelect
              containerClassName="w-auto min-w-[210px] max-1.5xl:min-w-0 max-sm:basis-full"
              placeholder={t('common:allUsages')}
              aria-label={t('common:usage')}
              options={getUsageFilterOptions(t)}
              value={type === '0' ? '' : type}
              onValueChange={searchByTypes}
            />
            <div className="flex justify-end max-sm:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <StructureFilter
            t={t}
            isLoadingAllCompanies={isLoadingAllCompanies}
            isLoadingAllBranches={isLoadingAllBranches}
            companyOptions={mapFilterSelect(allCompanies?.data)}
            branchOptions={mapFilterSelect(allBranches?.data)}
            displayCompanyFilter={isAdmin || isGroupManager || isCompanyManager}
            displayBranchFilter
            startDate={startDate}
            endDate={endDate}
            isWhiteMark={isWhiteMark}
            companyId={companyId}
            branchId={branchId}
            searchBeginDate={searchBeginDate}
            searchEndDate={searchEndDate}
            searchWhiteMark={searchWhiteMark}
            searchByCompany={searchByCompany}
            searchByBranch={searchByBranch}
          />
        </AdvancedFilterPanel>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalItems={isLoadingAllData ? undefined : allData?.data?.['total']}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
